import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Member, Service, ServiceBooking, StandaloneServiceBooking, BuildingOpeningDay } from '../models';
import { datePastValidator } from '../validators/date-past.directive';
import { outOfOpeningDays } from '../validators/out-of-opening-days.directive';

@Injectable()
/**
 * @requires
 */
export class StandaloneServiceBookingFormService {

  get owner(): Member {
    return this._owner.value;
  }

  set owner(owner: Member) {
    this._owner.patchValue(owner);
  }

  get referenceOpeningDays(): BuildingOpeningDay[] {
    return this._referenceOpeningDays;
  }
  set referenceOpeningDays(days: BuildingOpeningDay[]) {
    this._referenceOpeningDays = days;
    this.formGroup.get('date').setValidators([
      Validators.required,
      outOfOpeningDays(this._referenceOpeningDays),
      datePastValidator(),
    ]);
  }

  get referenceServiceBookings(): ServiceBooking[] {
    return this._referenceServiceBookings;
  }
  set referenceServiceBookings(servicesBookings: ServiceBooking[]) {
    this._referenceServiceBookings = servicesBookings;
  }

  get referenceServices(): Service[] {
    return this._referenceServices;
  }
  set referenceServices(services: Service[]) {
    this._referenceServices = services;
  }

  get service(): Service {
    return this._referenceServices.find(service => service.name === this._service.value);
  }

  set service(service: Service) {
    this._service.patchValue(service.name);
  }

  formGroup: FormGroup;

  private _owner: FormControl = new FormControl('', [
    Validators.required,
  ]);

  private _referenceOpeningDays: BuildingOpeningDay[];

  private _referenceServiceBookings: ServiceBooking[];

  private _referenceServices: Service[];

  private _service: FormControl = new FormControl('', [
    Validators.required,
    this.unknownServiceValidator(),
  ]);

  constructor(
    private formBuilder: FormBuilder,
  ) {
    this.formGroup = this.formBuilder.group({
      quantity: ['', [Validators.required, Validators.min(1), this.quantityLimitExceeded()]],
      date: ['', [Validators.required, outOfOpeningDays(this._referenceOpeningDays), datePastValidator()]],
      owner: this._owner,
      service: this._service,
      isInvoiceable: ['', [Validators.required]],
    });
  }

  getStandaloneServiceBookingFromForm(
    form: FormGroup,
    serviceBooking: StandaloneServiceBooking = new StandaloneServiceBooking()): StandaloneServiceBooking {
    Object.keys(form).forEach(key => {
      switch (key) {
        case 'service':
          serviceBooking['service'] = this.service['@id'];
          break;
        case 'owner':
          serviceBooking['owner'] = this.owner['@id'];
          break;
        case 'date':
          serviceBooking[key] = new Date(form[key]).toDateString();
          break;
        default:
          serviceBooking[key] = form[key];
          break;
      }
    });

    return serviceBooking;
  }

  private quantityLimitExceeded(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!this.formGroup ||
        !this.formGroup.get('date') ||
        !this._referenceServiceBookings ||
        this.formGroup.get('date').invalid === true) {
        return null;
      }

      let bookedQuantity = 0;
      for (const booking of this._referenceServiceBookings) {
        bookedQuantity += booking.quantity;
      }
      const totalQuantity = bookedQuantity + this.formGroup.get('quantity').value;

      return totalQuantity > this.service.quantityLimitUser ? {quantityLimitExceeded: {value: control.value}} : null;
    };
  }

  private unknownServiceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!this._referenceServices) {
        return null;
      }
      for (const service of this._referenceServices) {
        if (service.name === control.value) {
          return null;
        }
      }
      return {unknownService: {value: control.value}};
    };
  }
}
